import { useEffect, useState } from "react";
import { Download, Loader2, Lock } from "lucide-react";
import { API_BASE } from "@/lib/api";

export default function AdminLeads() {
  const [token, setToken] = useState("");
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Early-access leads — KeepThis! admin";
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { m.remove(); };
  }, []);

  const call = async (path) => {
    const res = await fetch(`${API_BASE}${path}`, { headers: { "X-Admin-Token": token } });
    if (res.status === 401) throw new Error("That admin token isn't right.");
    if (!res.ok) throw new Error("Something went wrong. Please try again.");
    return res;
  };

  const load = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      setSummary(await (await call("/admin/leads/summary")).json());
    } catch (err) {
      setError(err.message);
      setSummary(null);
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      const blob = await (await call("/admin/leads/export")).blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `keepthis-early-access-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-cream px-5 py-16">
      <div data-testid="admin-leads-page" className="w-full max-w-md rounded-[2rem] bg-white p-8 shadow-[0_30px_80px_-35px_rgba(18,52,90,0.5)]">
        <img src="/images/logo-word.webp" alt="KeepThis!" className="h-7 w-auto" />
        <h1 className="mt-6 font-heading text-2xl font-black text-navy">Early-access leads</h1>
        <form onSubmit={load} className="mt-6 space-y-3">
          <label htmlFor="admin-token" className="flex items-center gap-2 font-heading text-sm font-extrabold text-navy"><Lock className="h-4 w-4" /> Admin token</label>
          <input id="admin-token" data-testid="admin-token-input" type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)} className="h-12 w-full rounded-full bg-cream px-5 text-base text-navy focus:outline-none focus:ring-4 focus:ring-teal/30" />
          <button type="submit" data-testid="admin-token-submit" disabled={busy || !token} className="h-12 w-full rounded-full bg-navy font-heading font-extrabold text-cream disabled:opacity-60">Unlock</button>
        </form>
        {error && <p role="alert" data-testid="admin-error" className="mt-4 rounded-xl bg-coral/25 px-4 py-2.5 text-sm text-navy">{error}</p>}
        {summary && (
          <div className="mt-6 rounded-2xl bg-cream p-5">
            <p className="text-sm text-navy/75">Total signups</p>
            <p data-testid="admin-leads-total" className="font-heading text-4xl font-black text-navy">{summary?.total ?? 0}</p>
            <p className="mt-1 text-sm text-navy/75">{summary?.last_24h ?? 0} in the last 24 hours</p>
            <button type="button" data-testid="admin-export-csv" onClick={download} disabled={busy} className="mt-5 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-teal font-heading font-extrabold text-white disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download CSV
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
